// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/CrisisRoom.jsx
//  FORMATO 4 — CRISIS ROOM
//
//  Una situación límite, 45 segundos, y un jefe que te evalúa — no un maestro.
//
//  ── LA PEDAGOGÍA ────────────────────────────────────────────────────────────
//  Aprendizaje basado en tareas (TBLT) en estado puro. El cerebro codifica y
//  retiene mucho mejor el vocabulario cuando está anclado a una emoción fuerte
//  —resolver un conflicto— que en un entorno pasivo de estudio. El reloj y el
//  rojo no son estética: son el estrés que hace que lo dicho aquí se quede.
//
//  ── POR QUÉ EL VEREDICTO ES DE JEFE ─────────────────────────────────────────
//  "Tuviste un error en el pasado simple" no le sirve a nadie que acaba de
//  hablar con un cliente furioso. "Lograste calmarlo, pero usaste dos palabras
//  que te restaron autoridad" sí. La misión se juzga primero; el idioma, como
//  lo que costó — que es como funciona en la vida real.
//
//  Es el ÚNICO formato del Dojo que llama a la IA, porque es el único cuyo
//  criterio no se puede resolver con una tabla de respuestas correctas.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useRef, useState } from 'react';

import { createRecognition, isRecognitionAvailable } from '../utils/browserSpeech';
import { CRISIS, byContext, shuffle } from './dojoBank';
import { fxStart, fxTick, fxTimeout, fxCombo, fxMiss } from '../audio/fx';
import { evaluateCrisis } from './dojoApi';
import { useFeedback } from './useFeedback';
import FeedbackLayer from './FeedbackLayer';
import DojoResult from './DojoResult';

const MISSION_LABEL = {
  success: 'Misión cumplida',
  partial: 'Misión a medias',
  failed: 'Misión fallida',
};
const MISSION_CLASS = { success: 'dj-won', partial: 'dj-half', failed: 'dj-lost' };

export default function CrisisRoom({ ctx, onExit }) {
  const [deck] = useState(() => shuffle(byContext(CRISIS, ctx)).slice(0, 3));
  const [i, setI] = useState(0);
  const [phase, setPhase] = useState('brief');  // brief|live|judging|verdict|done
  const [left, setLeft] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [error, setError] = useState(null);
  const [wins, setWins] = useState(0);
  const [authority, setAuthority] = useState([]);

  const { flash, floats, fire } = useFeedback();

  const recRef = useRef(null);
  const tickRef = useRef(null);
  const startedAt = useRef(0);
  const finalText = useRef('');
  const leftRef = useRef(0);
  // Cerrojo: el reloj y el boton "Termine antes" pueden dispararse en el mismo
  // instante. Sin esto se pagarian DOS llamadas de IA por la misma respuesta.
  const judged = useRef(false);

  const item = deck[i];
  const canHear = isRecognitionAvailable();

  const cleanup = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    recRef.current = null;
    clearInterval(tickRef.current);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  /* ── Enviar a juicio ───────────────────────────────────────────────────── */
  const judge = useCallback(async (text) => {
    if (judged.current) return;
    judged.current = true;

    setPhase('judging');
    setError(null);
    const secs = Math.round((performance.now() - startedAt.current) / 1000);

    try {
      const data = await evaluateCrisis({
        scenarioId: item.id,
        transcript: text,
        seconds: secs,
      });
      setVerdict(data);
      setPhase('verdict');

      if (data.mission === 'success') { setWins((w) => w + 1); fxCombo(5); fire(true, '✓ MISIÓN'); }
      else if (data.mission === 'partial') { fxCombo(2); fire(true, '~ A MEDIAS'); }
      else { fxMiss(); fire(false, '✕ FALLIDA'); }

      if (typeof data.authority === 'number') setAuthority((a) => [...a, data.authority]);
    } catch (e) {
      setError(e.message);
      setPhase('verdict');
    }
  }, [item, fire]);

  /* ── El reloj ──────────────────────────────────────────────────────────── */
  const start = () => {
    if (!canHear) {
      setError('Este formato necesita reconocimiento de voz. Usa Chrome o Edge.');
      setPhase('verdict');
      return;
    }

    fxStart();
    judged.current = false;
    setTranscript('');
    finalText.current = '';
    setVerdict(null);
    setError(null);
    setLeft(item.seconds);
    setPhase('live');
    startedAt.current = performance.now();

    const rec = createRecognition({
      onPartial: (t) => { finalText.current = t; setTranscript(t); },
      onFinal: (t) => { finalText.current = t || finalText.current; },
      onError: () => { /* se juzga con lo que haya */ },
    });
    recRef.current = rec;
    rec?.start();

    // El tic vive en un ref y NO dentro del updater de `setLeft`: React puede
    // reejecutar un updater (StrictMode lo hace siempre), y disparar la IA
    // desde ahi significaria pagar la evaluacion dos veces.
    leftRef.current = item.seconds;
    tickRef.current = setInterval(() => {
      leftRef.current -= 1;
      setLeft(leftRef.current);

      if (leftRef.current <= 0) {
        clearInterval(tickRef.current);
        fxTimeout();
        try { recRef.current?.stop(); } catch { /* ya detenido */ }
        judge(finalText.current);
        return;
      }
      if (leftRef.current <= 10) fxTick(leftRef.current <= 5);
    }, 1000);
  };

  const stopEarly = () => {
    clearInterval(tickRef.current);
    try { recRef.current?.stop(); } catch { /* ya detenido */ }
    judge(finalText.current);
  };

  const next = () => {
    cleanup();
    if (i + 1 >= deck.length) { setPhase('done'); return; }
    setI((n) => n + 1);
    setPhase('brief');
  };

  /* ── Render ────────────────────────────────────────────────────────────── */
  if (phase === 'done') {
    const avgAuth = authority.length
      ? Math.round(authority.reduce((a, b) => a + b, 0) / authority.length)
      : null;
    return (
      <DojoResult
        format="crisis"
        score={wins}
        total={deck.length}
        headline={`${wins}/${deck.length}`}
        label="Misiones cumplidas"
        recap={[
          { v: wins, k: 'misiones' },
          { v: avgAuth !== null ? `${avgAuth}/5` : '—', k: 'autoridad' },
          { v: deck.length, k: 'crisis' },
        ]}
        note="Lo que dijiste bajo presión se queda. Ese es el punto de este formato: el estrés fija el vocabulario mucho mejor que el repaso."
        onAgain={() => window.location.reload()}
        onExit={onExit}
      />
    );
  }

  return (
    <>
      <FeedbackLayer flash={flash} floats={floats} />

      <div className="dj-hud">
        <button type="button" className="dj-quit" onClick={() => { cleanup(); onExit(); }}>← Salir</button>
        <span className="dj-badge">Crisis Room</span>
        <span className="dj-spacer" />
        <span className="dj-progress">{item.ctx.replace('_', ' ')}</span>
        <span className="dj-score-badge">{i + 1}/{deck.length}</span>
      </div>

      {phase === 'brief' && (
        <>
          <div className="dj-crisis">
            <span className="dj-role">{item.role}</span>
            <p className="dj-scene">{item.scene}</p>
            <p className="dj-mission">
              <b>Tu misión</b>
              {item.mission}
            </p>
          </div>
          <div className="dj-actions" style={{ marginTop: 16 }}>
            <button type="button" className="dj-btn" onClick={start}>
              GO — {item.seconds} segundos ⏱
            </button>
          </div>
        </>
      )}

      {phase === 'live' && (
        <>
          <div className="dj-crisis">
            <span className="dj-role">{item.role}</span>
            <p className="dj-scene">{item.scene}</p>
          </div>
          <p className={`dj-clock dj-num ${left <= 10 ? 'dj-urgent' : ''}`}>
            0:{String(left).padStart(2, '0')}
          </p>
          <div className="dj-transcript">{transcript}</div>
          <div className="dj-actions" style={{ marginTop: 14 }}>
            <button type="button" className="dj-btn dj-btn-ghost" onClick={stopEarly}>
              ■ Terminé antes
            </button>
          </div>
        </>
      )}

      {phase === 'judging' && (
        <div className="dj-stage">
          <div className="dj-spin" />
          <p className="dj-section-lbl">El jefe está leyendo tu respuesta…</p>
        </div>
      )}

      {phase === 'verdict' && (
        <>
          {error ? (
            <div className="dj-fail">{error}</div>
          ) : verdict?.evaluated === false ? (
            <div className="dj-fail">
              No pudimos evaluar esta respuesta ahora mismo. Tu transcripción no
              se perdió, pero no vamos a inventarte una calificación.
            </div>
          ) : verdict ? (
            <div className={`dj-verdict-box ${MISSION_CLASS[verdict.mission] || 'dj-half'}`}>
              <p className="dj-mission-state">
                {MISSION_LABEL[verdict.mission] || 'Misión evaluada'}
                {typeof verdict.authority === 'number' && (
                  <span>autoridad {verdict.authority}/5</span>
                )}
              </p>
              <p className="dj-boss">{verdict.verdict_es}</p>
              {verdict.cost?.length > 0 && (
                <ul className="dj-cost">
                  {verdict.cost.map((c) => <li key={c}>{c}</li>)}
                </ul>
              )}
            </div>
          ) : null}

          {transcript && (
            <div className="dj-transcript" style={{ marginTop: 14 }}>
              <span className="dj-transcript-lbl">LO QUE DIJISTE</span>
              {transcript}
            </div>
          )}

          <div className="dj-actions" style={{ marginTop: 16 }}>
            <button type="button" className="dj-btn" onClick={next}>
              {i + 1 >= deck.length ? 'Ver resultado →' : 'Siguiente crisis →'}
            </button>
          </div>
        </>
      )}
    </>
  );
}
